import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "wouter";
import { ArrowUpRight, ArrowLeft, Calendar, Users, Filter } from "lucide-react";
import { useState, useEffect } from "react";
import { projects, categories } from "@/data/projects";

export default function AllProjects() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredProjects = activeCategory === "all"
    ? projects
    : projects.filter(p => p.category === activeCategory);

  const getProjectTitle = (project: typeof projects[0]) => t(project.title);
  const getProjectDescription = (project: typeof projects[0]) => t(project.description);

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)',
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Link href="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                {isRTL ? 'الرئيسية' : 'Back to Home'}
              </Button>
            </Link>
          </motion.div>

          <motion.div
            className={`max-w-3xl ${isRTL ? 'text-right mr-auto' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.span
              className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {isRTL ? 'معرض الأعمال' : 'Portfolio'}
            </motion.span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="gradient-text">{t("portfolioTitle")}</span>
            </h1>

            <p className="text-xl text-muted-foreground leading-relaxed">
              {t("portfolioDescription")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-16 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className={`flex items-center gap-4 overflow-x-auto pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === category.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {language === 'ar' ? category.ar : category.en}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={fadeInUp}
                className="group"
              >
                <Link href={`/projects/${project.id}`}>
                  <div className="overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/20 transition-all duration-500 cursor-pointer hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 flex flex-col h-full">
                    {/* Image Section */}
                    <div className="relative overflow-hidden h-[220px]">
                      <img
                        src={project.image}
                        alt={getProjectTitle(project)}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />

                      {/* Featured Badge */}
                      {project.featured && (
                        <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'}`}>
                          <span className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-full shadow-lg">
                            {isRTL ? 'مميز' : 'Featured'}
                          </span>
                        </div>
                      )}

                      {/* Category Badge */}
                      <div className={`absolute bottom-3 ${isRTL ? 'right-3' : 'left-3'}`}>
                        <span className="px-3 py-1.5 text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground rounded-full border border-border/50">
                          {project.tags[0]}
                        </span>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className={`p-5 flex flex-col gap-3 flex-1 ${isRTL ? 'text-right' : ''}`}>
                      {/* Meta Info */}
                      <div className={`flex items-center gap-4 text-xs text-muted-foreground ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <span className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar className="h-3.5 w-3.5" />
                          {isRTL ? project.durationAr : project.duration}
                        </span>
                        <span className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Users className="h-3.5 w-3.5" />
                          {isRTL ? project.teamSizeAr : project.teamSize}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight">
                        {getProjectTitle(project)}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">
                        {getProjectDescription(project)}
                      </p>

                      {/* Tags */}
                      <div className={`flex flex-wrap gap-1.5 ${isRTL ? 'justify-end' : ''}`}>
                        {project.tags.slice(1, 4).map((tag) => (
                          <span
                            key={tag}
                            className="text-[11px] px-2 py-0.5 rounded-md bg-muted/80 text-muted-foreground font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className={`flex items-center justify-between pt-3 border-t border-border/40 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-2 text-primary font-semibold text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span>{t("viewProject")}</span>
                          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-muted-foreground text-lg">
                {isRTL ? 'لا توجد مشاريع في هذه الفئة' : 'No projects in this category'}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {isRTL ? 'هل لديك مشروع في ذهنك؟' : 'Have a project in mind?'}
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {isRTL
                ? 'دعنا نحول فكرتك إلى واقع رقمي. تواصل معنا اليوم!'
                : "Let's turn your idea into digital reality. Get in touch today!"}
            </p>
            <Link href="/#contact">
              <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-xl">
                {t("getInTouch")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
